import type { components } from "@majitap/sdk";
import { allPages } from "@majitap/ui";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";
import type { Tone } from "./labels";

type Schemas = components["schemas"];
export type Integration = Schemas["Integration"];
export type Kind = Integration["kind"];
export type ProviderForm = Schemas["ProviderFormList"]["data"][number];
export type Field = ProviderForm["config"][number];
export type Link = Schemas["AccountingLink"];
export type TaxLink = Schemas["TaxLink"];
export type RegisteredTaxItem = Schemas["RegisteredTaxItem"];
export type TaxCode = Schemas["RegisterTaxItemBody"]["tax_type_code"];

export const KIND: Record<Kind, string> = {
  payment: "Payments",
  sms: "SMS",
  email: "Email",
  accounting: "Accounting",
  tax: "Tax (eTIMS)",
  sensor: "Tank sensors",
  geocoding: "Address search",
  routing: "Drive times (ETA)",
};

export const INTEGRATION_STATUS: Record<Integration["status"], [label: string, tone: Tone]> = {
  active: ["On", "success"],
  inactive: ["Off", "neutral"],
  error: ["Needs attention", "danger"],
};

export const SYNC_STATUS: Record<Link["sync_status"], [label: string, tone: Tone]> = {
  pending: ["Waiting", "warning"],
  synced: ["Synced", "success"],
  failed: ["Failed", "danger"],
  skipped: ["Not sent", "neutral"],
};

export const integrationsQuery = queryOptions({
  queryKey: ["integrations"],
  queryFn: async () => (await ok(api.GET("/v1/admin/integrations"))).data,
});

export const providerFormsQuery = queryOptions({
  queryKey: ["integrations", "providers"],
  staleTime: 10 * 60_000,
  queryFn: async () => (await ok(api.GET("/v1/admin/integrations/providers"))).data,
});

export const integrationQuery = (id: string) =>
  queryOptions({
    queryKey: ["integrations", id],
    queryFn: () => ok(api.GET("/v1/admin/integrations/{id}", { params: { path: { id } } })),
  });

/** Every product item, with its product's name, for the mapping tables. */
export const productItemsQuery = queryOptions({
  queryKey: ["catalog", "products", "items"],
  queryFn: async () => {
    const products = await allPages((cursor) =>
      ok(api.GET("/v1/admin/catalog/products", { params: { query: { cursor, limit: 100 } } })),
    );
    return products.flatMap((p) => p.items.map((item) => ({ ...item, product: p.name })));
  },
});

/** One QuickBooks integration's links of one kind (product items), or in one state (failed, waiting, not sent). */
export type Attention = "failed" | "pending" | "skipped";

export const accountingLinksQuery = (
  integrationId: string,
  filter: { entity_type?: string; sync_status?: Attention },
) =>
  queryOptions({
    queryKey: ["integrations", integrationId, "links", filter],
    queryFn: () =>
      allPages((cursor) =>
        ok(
          api.GET("/v1/admin/accounting/links", {
            params: { query: { integration_id: integrationId, ...filter, cursor, limit: 100 } },
          }),
        ),
      ),
  });

/** eTIMS links of one kind (product items), or in one state (failed, waiting, not sent). */
export const taxLinksQuery = (filter: { entity_type?: string; sync_status?: Attention }) =>
  queryOptions({
    queryKey: ["tax", "links", filter],
    queryFn: () =>
      allPages((cursor) =>
        ok(api.GET("/v1/admin/tax/links", { params: { query: { ...filter, cursor, limit: 100 } } })),
      ),
  });

/** What the connected QuickBooks company has, to pick from instead of typing its ids. */
export type QuickbooksPick = "accounts" | "tax_codes" | "items";
export type QuickbooksOption = { value: string; label: string; type: string | null; product_item_ids: string[] };
export const quickbooksListQuery = (integrationId: string, list: QuickbooksPick) =>
  queryOptions({
    queryKey: ["accounting", "quickbooks", integrationId, list],
    staleTime: 60_000,
    queryFn: async (): Promise<QuickbooksOption[]> => {
      const path = { params: { path: { integration_id: integrationId } } };
      if (list === "accounts") {
        const { data } = await ok(api.GET("/v1/admin/accounting/quickbooks/{integration_id}/accounts", path));
        return data.map((a) => ({
          value: a.id,
          label: [a.number, a.name].filter(Boolean).join(" "),
          type: a.type,
          product_item_ids: [],
        }));
      }
      if (list === "tax_codes") {
        const { data } = await ok(api.GET("/v1/admin/accounting/quickbooks/{integration_id}/tax-codes", path));
        return data.map((t) => ({
          value: t.id,
          label: t.description ? `${t.name} · ${t.description}` : t.name,
          type: null,
          product_item_ids: [],
        }));
      }
      const { data } = await ok(api.GET("/v1/admin/accounting/quickbooks/{integration_id}/items", path));
      return data.map((i) => ({
        value: i.id,
        label: i.sku ? `${i.name} (${i.sku})` : i.name,
        type: i.type,
        product_item_ids: i.product_item_ids,
      }));
    },
  });

/** The items registered at DigiTax, asked of DigiTax each time, to map a product item to one. */
export const registeredTaxItemsQuery = (integrationId: string) =>
  queryOptions({
    queryKey: ["tax", "registered", integrationId],
    queryFn: async () =>
      (
        await ok(
          api.GET("/v1/admin/tax/{integration_id}/items/registered", {
            params: { path: { integration_id: integrationId } },
          }),
        )
      ).data,
  });

/** KRA tax types, as the eTIMS register form offers them. */
export const TAX_TYPE: Record<TaxCode, string> = {
  A: "A · Exempt",
  B: "B · 16% VAT",
  C: "C · Zero-rated",
  D: "D · Not VAT",
  E: "E · 8% VAT",
};

/** A config value as typed in a form: lists are typed comma-separated. */
export const configText = (value: unknown): string =>
  Array.isArray(value) ? value.join(", ") : value === undefined || value === null ? "" : String(value);

/**
 * The config to send. A save replaces the whole config, so it starts from what's stored: keys the
 * form doesn't show stay (QuickBooks keeps its company id there); a field typed empty is removed.
 */
export function configOf(
  fields: Field[],
  typed: Record<string, string>,
  stored: Record<string, unknown> = {},
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...stored };
  for (const f of fields) {
    const v = (typed[f.key] ?? "").trim();
    if (!v) delete out[f.key];
    else
      out[f.key] =
        f.type === "list"
          ? v
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean)
          : v;
  }
  return out;
}

/** The config as the form starts: each field's stored value, as text. */
export const typedConfig = (fields: Field[], stored: Record<string, unknown>) =>
  Object.fromEntries(fields.map((f) => [f.key, configText(stored[f.key])]));

/**
 * What saving the settings form sends. Credentials go only when typed (then all of them), so a
 * save that leaves them empty keeps what's stored; nothing stored is ever read back into the form.
 */
export function updateBodyOf(
  i: Pick<Integration, "config">,
  form: Pick<ProviderForm, "config" | "secrets">,
  typed: { name: string; sandbox: boolean; config: Record<string, string>; secrets: Record<string, string> },
): Schemas["UpdateIntegrationBody"] {
  const secrets = secretsOf(form.secrets, typed.secrets);
  return {
    name: typed.name.trim(),
    is_sandbox: typed.sandbox,
    config: configOf(form.config, typed.config, i.config),
    ...(secrets ? { secrets } : {}),
  };
}

/** QuickBooks is connected once Intuit's refresh token is stored. */
export const quickbooksConnected = (i: Pick<Integration, "provider" | "secret_keys">) =>
  i.provider === "quickbooks" && i.secret_keys.includes("refresh_token");

/** What a record offers, by provider. */
export const integrationActionsFor = (i: Pick<Integration, "provider" | "secret_keys" | "status">) => ({
  registerTill: i.provider === "mpesa" && i.status === "active",
  connect: i.provider === "quickbooks" && !quickbooksConnected(i),
  disconnect: quickbooksConnected(i),
});

/** The provider's form; for a provider the API has no form for, the stored config keys as plain fields. */
export function formFor(forms: ProviderForm[], i: Pick<Integration, "provider" | "kind" | "config">): ProviderForm {
  return (
    forms.find((f) => f.provider === i.provider && f.kind === i.kind) ?? {
      kind: i.kind,
      provider: i.provider,
      label: i.provider,
      config: Object.keys(i.config).map((key) => ({ key, label: key.replace(/_/g, " ") })),
      secrets: [],
    }
  );
}

/**
 * The secrets to send, or nothing. Secrets are write-only: a form starts empty and never shows
 * what's stored. Sending any replaces them all, so they're sent only when every required one is
 * typed (`secretsReady`).
 */
export function secretsOf(fields: Field[], typed: Record<string, string>): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const f of fields) {
    const v = (typed[f.key] ?? "").trim();
    if (v) out[f.key] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

/** Either no secret typed (keep what's stored), or every required one. */
export const secretsReady = (fields: Field[], typed: Record<string, string>) => {
  const any = fields.some((f) => (typed[f.key] ?? "").trim());
  return !any || fields.filter((f) => f.required).every((f) => (typed[f.key] ?? "").trim());
};

/** Product items beside their mapping in one integration (QuickBooks or eTIMS), for the mapping table. */
export function itemMappings<I extends { id: string }, L extends { entity_id: string }>(items: I[], links: L[]) {
  const byItem = new Map(links.map((l) => [l.entity_id, l]));
  return items.map((item) => ({ id: item.id, item, link: byItem.get(item.id) ?? null }));
}

const SYNCABLE = ["customer", "business", "product_item", "invoice", "payment"] as const;
/** The records QuickBooks sync can send again. */
export const syncable = (type: string): type is (typeof SYNCABLE)[number] =>
  (SYNCABLE as readonly string[]).includes(type);

/** What a failed accounting or tax record is, as people call it. */
export const ENTITY: Record<string, string> = {
  customer: "Customer",
  business: "Business",
  product_item: "Product item",
  invoice: "Invoice",
  invoice_credit_note: "Credit note",
  payment: "Payment",
};

const integrationPath = (id: string) => ({ params: { path: { integration_id: id } } });

export const connectionActions = {
  create: (body: Schemas["CreateIntegrationBody"]) => ok(api.POST("/v1/admin/integrations", { body })),
  update: (id: string, body: Schemas["UpdateIntegrationBody"]) =>
    ok(api.PATCH("/v1/admin/integrations/{id}", { params: { path: { id } }, body })),
  registerTillUrls: () => ok(api.POST("/v1/admin/payments/mpesa/register-till-urls")),
  connectQuickbooks: (id: string) =>
    ok(api.POST("/v1/admin/accounting/quickbooks/{integration_id}/connect", integrationPath(id))),
  disconnectQuickbooks: (id: string) =>
    ok(api.POST("/v1/admin/accounting/quickbooks/{integration_id}/disconnect", integrationPath(id))),
  mapQuickbooksItem: (id: string, itemId: string, externalId: string) =>
    ok(
      api.PUT("/v1/admin/accounting/quickbooks/{integration_id}/items/{product_item_id}", {
        params: { path: { integration_id: id, product_item_id: itemId } },
        body: { external_id: externalId },
      }),
    ),
  syncAgain: (entityType: Schemas["RequestSyncBody"]["entity_type"], entityId: string) =>
    ok(api.POST("/v1/admin/accounting/sync", { body: { entity_type: entityType, entity_id: entityId } })),
  resubmitTax: (invoiceId: string, document: "sale" | "credit_note") =>
    ok(
      api.POST("/v1/admin/tax/invoices/{invoice_id}/resubmit", {
        params: { path: { invoice_id: invoiceId } },
        body: { document },
      }),
    ),
  mapTaxItem: (id: string, itemId: string, externalId: string) =>
    ok(
      api.PUT("/v1/admin/tax/{integration_id}/items/{product_item_id}", {
        params: { path: { integration_id: id, product_item_id: itemId } },
        body: { external_id: externalId },
      }),
    ),
  registerTaxItem: (id: string, itemId: string, body: Schemas["RegisterTaxItemBody"]) =>
    ok(
      api.POST("/v1/admin/tax/{integration_id}/items/{product_item_id}/register", {
        params: { path: { integration_id: id, product_item_id: itemId } },
        body,
      }),
    ),
};
